import { Capacitor } from '@capacitor/core';

export const GOOGLE_CLIENT_ID: string = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

const GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const GSI_SCRIPT_ID = 'roade-google-identity-services';

const isNative = Capacitor.isNativePlatform();

export const isGoogleSignInConfigured = (): boolean => Boolean(GOOGLE_CLIENT_ID);

/**
 * `credential` is the Google ID token. It is the only field the backend consumes.
 */
export interface GoogleCredentialResponse {
  credential?: string;
}

interface GoogleButtonOptions {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'small' | 'medium' | 'large';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  width?: number;
  locale?: string;
}

interface GoogleIdentityServices {
  accounts: {
    id: {
      initialize: (config: {
        client_id: string;
        callback: (response: GoogleCredentialResponse) => void;
        auto_select?: boolean;
        button_auto_select?: boolean;
        cancel_on_tap_outside?: boolean;
        use_fedcm_for_button?: boolean;
        use_fedcm_for_prompt?: boolean;
      }) => void;
      renderButton: (parent: HTMLElement, options: GoogleButtonOptions) => void;
      disableAutoSelect: () => void;
    };
  };
}

declare global {
  interface Window {
    google?: GoogleIdentityServices;
  }
}

let gsiScriptLoad: Promise<GoogleIdentityServices> | null = null;
let initializedClientId: string | null = null;
let activeCredentialHandler: ((idToken: string) => void) | null = null;
let activeFailureHandler: ((error: Error) => void) | null = null;

/**
 * Loads Google Identity Services once per page. The script tag is injected on demand
 * rather than shipped in index.html so that a visitor who never touches Google
 * sign-in never contacts Google.
 */
const loadGoogleIdentityServices = (): Promise<GoogleIdentityServices> => {
  if (window.google?.accounts?.id) {
    return Promise.resolve(window.google);
  }
  if (gsiScriptLoad) {
    return gsiScriptLoad;
  }

  gsiScriptLoad = new Promise<GoogleIdentityServices>((resolve, reject) => {
    const existing = document.getElementById(GSI_SCRIPT_ID) as HTMLScriptElement | null;
    const onLoad = () => {
      if (window.google?.accounts?.id) {
        resolve(window.google);
      } else {
        reject(new Error('Google Identity Services loaded without the expected API'));
      }
    };
    const onError = () => reject(new Error('Google Identity Services could not be loaded'));

    if (existing) {
      existing.addEventListener('load', onLoad);
      existing.addEventListener('error', onError);
      return;
    }

    const script = document.createElement('script');
    script.id = GSI_SCRIPT_ID;
    script.src = GSI_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);
    document.head.appendChild(script);
  }).catch(error => {
    // Allow a later retry after a transient network failure.
    gsiScriptLoad = null;
    throw error;
  });

  return gsiScriptLoad;
};

/**
 * Renders Google's own button into `container`.
 *
 * Google's rendered button is used instead of a look-alike: it is the supported way to
 * obtain an ID token without One Tap, it satisfies Google's branding requirements, and
 * it avoids the popup-blocker problems of a self-triggered flow.
 *
 * @returns a cleanup function that empties the container.
 */
export const renderGoogleButton = async (
  container: HTMLElement,
  onCredential: (idToken: string) => void,
  onFailure: (error: Error) => void,
  options: GoogleButtonOptions = {},
  signal?: AbortSignal,
): Promise<() => void> => {
  if (!isGoogleSignInConfigured()) {
    throw new Error('VITE_GOOGLE_CLIENT_ID is not configured');
  }

  const google = await loadGoogleIdentityServices();
  if (signal?.aborted) {
    return () => undefined;
  }
  activeCredentialHandler = onCredential;
  activeFailureHandler = onFailure;
  if (initializedClientId !== GOOGLE_CLIENT_ID) {
    google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: (response: GoogleCredentialResponse) => {
        if (response.credential) {
          activeCredentialHandler?.(response.credential);
        } else {
          activeFailureHandler?.(new Error('Google did not return an identity token'));
        }
      },
      auto_select: false,
      button_auto_select: false,
      cancel_on_tap_outside: true,
      use_fedcm_for_button: false,
    });
    initializedClientId = GOOGLE_CLIENT_ID;
  }

  container.replaceChildren();
  google.accounts.id.renderButton(container, {
    theme: 'outline',
    text: 'continue_with',
    shape: 'rectangular',
    logo_alignment: 'left',
    locale: 'ro',
    ...options,
    type: 'standard',
    // Google does not personalize medium or small standard buttons with account data.
    size: 'medium',
  });

  return () => {
    container.replaceChildren();
    if (activeCredentialHandler === onCredential) {
      activeCredentialHandler = null;
      activeFailureHandler = null;
    }
  };
};

interface NativeGoogleAuthPlugin {
  initialize: (options: { clientId: string; scopes: string[]; grantOfflineAccess: boolean }) => void;
  signIn: () => Promise<{ authentication?: { idToken?: string } }>;
}

/**
 * The specifier is held in a variable so neither TypeScript nor the bundler tries to
 * resolve it. The plugin is an Android/iOS-only dependency: the web build must succeed
 * without it installed, and this code path only runs on a native platform.
 */
const NATIVE_GOOGLE_AUTH_MODULE = '@codetrix-studio/capacitor-google-auth';

export const requestNativeGoogleIdToken = async (): Promise<string> => {
  const module = await import(/* @vite-ignore */ NATIVE_GOOGLE_AUTH_MODULE)
    .catch(() => {
      throw new Error('The native Google sign-in plugin is not installed');
    }) as { GoogleAuth?: NativeGoogleAuthPlugin };

  const googleAuth = module.GoogleAuth;
  if (!googleAuth) {
    throw new Error('The native Google sign-in plugin is not installed');
  }

  googleAuth.initialize({
    clientId: GOOGLE_CLIENT_ID,
    scopes: ['profile', 'email'],
    grantOfflineAccess: false,
  });

  const result = await googleAuth.signIn();
  const idToken = result?.authentication?.idToken;
  if (!idToken) {
    throw new Error('Google did not return an identity token');
  }
  return idToken;
};

export const isNativeGoogleSignIn = (): boolean => isNative;

/**
 * Clears Google's cached "auto select" choice so the next sign-in asks again. Called on
 * logout; a missing or unloaded SDK is not an error here.
 */
export const forgetGoogleSession = (): void => {
  try {
    window.google?.accounts?.id?.disableAutoSelect();
  } catch {
    // No Google session to forget.
  }
};
